/** Whether the client can use Business tab / business-scoped features. */
export function clientHasBusinessAccess(client) {
  if (!client) return false;
  if (Array.isArray(client.businesses) && client.businesses.length > 0) {
    return true;
  }
  if (client.business_registered === true) return true;
  if (String(client.business_name || "").trim()) return true;
  const t = String(client.client_type ?? "").trim().toLowerCase();
  return t === "business" || t.startsWith("business");
}

export function isIndividualClientAccountType(client) {
  const t = String(client?.client_type ?? "").trim().toLowerCase();
  return !t.startsWith("business");
}

/** True when at least one business profile exists (API list or saved registration). */
export function clientHasRegisteredBusinesses(client, extraBusinesses = []) {
  const seen = new Set();
  const all = [...getClientBusinesses(client), ...(extraBusinesses || [])];
  return all.some((business) => {
    const name = String(business?.business_name || "").trim();
    if (!name) return false;
    const key = String(business?.client_business_id || name);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Client is a business account (has registered business profile(s)). */
export function isBusinessAccount(client) {
  return clientHasBusinessAccess(client);
}

export function promoteClientToBusinessAccount(client, businesses = []) {
  const list = Array.isArray(businesses)
    ? businesses
    : getClientBusinesses(client);
  if (!list.length && !clientHasBusinessAccess(client)) {
    return client;
  }
  return mergeBusinessesIntoClient(
    { ...client, client_type: "Business", business_registered: true },
    list,
  );
}

export function getClientBusinesses(client) {
  if (!client) return [];
  if (Array.isArray(client.businesses) && client.businesses.length > 0) {
    return client.businesses;
  }
  if (!clientHasBusinessAccess(client)) return [];
  // Legacy flat fields → synthetic single business (no id until API refresh)
  return [
    {
      client_business_id: client.primary_client_business_id || null,
      client_id: client.client_id,
      business_name: client.business_name || "",
      business_type: client.business_type || "",
      business_phone: client.business_phone || "",
      business_address_1: client.business_address_1 || "",
      business_address_2: client.business_address_2 || "",
      business_country_id: client.business_country_id,
      business_state_id: client.business_state_id,
      business_city_id: client.business_city_id || "",
      business_zipcode: client.business_zipcode || "",
      is_primary: true,
      status: 1,
    },
  ];
}

export function getPrimaryClientBusiness(client) {
  const list = getClientBusinesses(client);
  return list.find((b) => b.is_primary) || list[0] || null;
}

export function mergeBusinessesIntoClient(client, businesses) {
  if (!client) return client;
  const list = Array.isArray(businesses) ? businesses : [];
  const primary = list.find((b) => b.is_primary) || list[0];
  const hasBusinesses = list.length > 0 || clientHasBusinessAccess(client);
  return {
    ...client,
    businesses: list,
    business_registered: list.length > 0,
    ...(hasBusinesses ? { client_type: "Business" } : {}),
    ...(primary
      ? {
          business_name: primary.business_name,
          business_type: primary.business_type,
          business_phone: primary.business_phone,
          business_address_1: primary.business_address_1,
          business_address_2: primary.business_address_2,
          business_country_id: primary.business_country_id,
          business_state_id: primary.business_state_id,
          business_city_id: primary.business_city_id,
          business_zipcode: primary.business_zipcode,
          primary_client_business_id: primary.client_business_id,
        }
      : {}),
  };
}
